import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';
import { getJoditConfig } from '../../utils/joditConfig';
import ConfirmModal from '../../components/ConfirmModal';

const EditState = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const aboutEditor = useRef(null);
  const [showBackModal, setShowBackModal] = useState(false);
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;
  
  const [formData, setFormData] = useState({
    name: '',
    latitude: '',
    longitude: '',
    author: '',
    metaTitle: '',
    metaScript: '',
    metaKeyword: '',
    metaDescription: '',
    countryId: ''
  });

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [faqs, setFaqs] = useState([{ question: '', answer: '' }]);
  const [recentFaqs, setRecentFaqs] = useState([]);
  const [keywordLinks, setKeywordLinks] = useState([{ keyword: '', link: '' }]);
  const [about, setAbout] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const editorConfig = useMemo(() => getJoditConfig(), []);

  useEffect(() => {
    fetchStateData();

    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setShowBackModal(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [id]);

  const fetchStateData = async () => {
    try {
      const response = await apiService.get(`stateEdit/${id}`);
      const data = response.data;
      
      setFormData({
        name: data.name || '',
        latitude: data.latitude || '',
        longitude: data.longitude || '',
        author: data.author || '',
        metaTitle: data.metaTitle || '',
        metaScript: data.metaScript || '',
        metaKeyword: data.metaKeyword || '',
        metaDescription: data.metaDescription || '',
        countryId: data.countryId || ''
      });

      setAbout(data.about || '');
      setImagePreview(data.image ? `${mediaUrl}${data.image}` : '');
      setFaqs(data.faq && data.faq.length > 0 ? data.faq : [{ question: '', answer: '' }]);
      setRecentFaqs(data.recentFaq && data.recentFaq.length > 0 ? data.recentFaq : []);
      setKeywordLinks(data.keywordLink && data.keywordLink.length > 0 ? data.keywordLink : [{ keyword: '', link: '' }]);
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch state data');
      console.error(error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be 5MB or less');
        e.target.value = '';
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const addFaq = () => setFaqs([...faqs, { question: '', answer: '' }]);
  const removeFaq = (index) => setFaqs(faqs.filter((_, i) => i !== index));
  const updateFaq = (index, field, value) => {
    const updated = [...faqs];
    updated[index][field] = value;
    setFaqs(updated);
  };

  const addRecentFaq = () => setRecentFaqs([...recentFaqs, { question: '', answer: '' }]);
  const removeRecentFaq = (index) => setRecentFaqs(recentFaqs.filter((_, i) => i !== index));
  const updateRecentFaq = (index, field, value) => {
    const updated = [...recentFaqs];
    updated[index][field] = value;
    setRecentFaqs(updated);
  };

  const addKeywordLink = () => setKeywordLinks([...keywordLinks, { keyword: '', link: '' }]);
  const removeKeywordLink = (index) => setKeywordLinks(keywordLinks.filter((_, i) => i !== index));
  const updateKeywordLink = (index, field, value) => {
    const updated = [...keywordLinks];
    updated[index][field] = value;
    setKeywordLinks(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    data.append('id', id);
    data.append('name', formData.name);
    data.append('latitude', formData.latitude);
    data.append('longitude', formData.longitude);
    data.append('author', formData.author);
    data.append('about', about);
    data.append('metaTitle', formData.metaTitle);
    data.append('metaScript', formData.metaScript);
    data.append('metaKeyword', formData.metaKeyword);
    data.append('metaDescription', formData.metaDescription);
    
    if (image) data.append('image', image);

    const filteredFaqs = faqs.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredFaqs.length === 0) {
      data.append('delete_all_faq', '1');
    } else {
      filteredFaqs.forEach((faq, i) => {
        data.append(`faq[${i}][question]`, faq.question || '');
        data.append(`faq[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredRecentFaqs = recentFaqs.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredRecentFaqs.length === 0) {
      data.append('delete_all_recentFaq', '1');
    } else {
      filteredRecentFaqs.forEach((faq, i) => {
        data.append(`recentFaq[${i}][question]`, faq.question || '');
        data.append(`recentFaq[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredKeywordLinks = keywordLinks.filter(k => k.keyword?.trim() || k.link?.trim());
    if (filteredKeywordLinks.length === 0) {
      data.append('delete_all_keywordLink', '1');
    } else {
      filteredKeywordLinks.forEach((kl, i) => {
        data.append(`keywordLink[${i}][keyword]`, kl.keyword || '');
        data.append(`keywordLink[${i}][link]`, kl.link || '');
      });
    }

    try {
      await apiService.postFormData('updateState', data);
      toast.success('State updated successfully');
      navigate(`/state/${formData.countryId}`);
    } catch (error) {
      toast.error('Failed to update state');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="bi bi-arrow-repeat animate-spin text-4xl text-[#009ef7]"></i>
      </div>
    );
  }

  return (
    <div className="p-0">
      <div className="mb-6">
        <nav className="text-sm mb-4">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-500">Location</span>
          <span className="mx-2 text-gray-400">/</span>
          <Link to={`/state/${formData.countryId}`} className="text-gray-500 hover:text-gray-700">States</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Edit State</span>
        </nav>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
              <div className="relative w-full max-w-xs h-44 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden mx-auto md:mx-0">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleImageChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
              </div>
            </div>
            <div className="md:col-span-7">
              <label className="block text-sm font-medium text-gray-700 mb-2">State Name <span className="text-red-500">*</span></label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter State name" required className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
              <input type="number" step="any" name="latitude" value={formData.latitude} onChange={handleInputChange} placeholder="Enter latitude" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
              <input type="number" step="any" name="longitude" value={formData.longitude} onChange={handleInputChange} placeholder="Enter longitude" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
              <input type="text" name="author" value={formData.author} onChange={handleInputChange} placeholder="Enter author name" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">About</label>
            <JoditEditor ref={aboutEditor} value={about} config={editorConfig} onBlur={newContent => setAbout(newContent)} />
          </div>

          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-900 mb-4">FAQ Section</label>
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question || ''} onChange={(e) => updateFaq(index, 'question', e.target.value)} placeholder="Enter FAQ question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer || ''} onChange={(e) => updateFaq(index, 'answer', e.target.value)} placeholder="Enter FAQ answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeFaq(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove FAQ</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addFaq} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
            </div>
          </div>

          {recentFaqs.length > 0 && (
            <div className="mb-6">
              <label className="block text-lg font-semibold text-gray-900 mb-4">Recent FAQ Section</label>
              {recentFaqs.map((faq, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                    <input type="text" value={faq.question || ''} onChange={(e) => updateRecentFaq(index, 'question', e.target.value)} placeholder="Enter Recent FAQ question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                    <textarea value={faq.answer || ''} onChange={(e) => updateRecentFaq(index, 'answer', e.target.value)} placeholder="Enter Recent FAQ answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                  </div>
                  <button type="button" onClick={() => removeRecentFaq(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove Recent FAQ</button>
                </div>
              ))}
              <div className="text-right">
                <button type="button" onClick={addRecentFaq} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add Recent FAQ</button>
              </div>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Keyword Link Section</label>
            {keywordLinks.map((kl, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Keyword</label>
                    <input type="text" value={kl.keyword || ''} onChange={(e) => updateKeywordLink(index, 'keyword', e.target.value)} placeholder="Enter keyword" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Link</label>
                    <input type="url" value={kl.link || ''} onChange={(e) => updateKeywordLink(index, 'link', e.target.value)} placeholder="Enter link URL" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                  </div>
                </div>
                <button type="button" onClick={() => removeKeywordLink(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addKeywordLink} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add Keyword Link</button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
            <input type="text" name="metaTitle" value={formData.metaTitle} onChange={handleInputChange} placeholder="Enter meta title" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Script (Schema)</label>
            <textarea name="metaScript" value={formData.metaScript} onChange={handleInputChange} placeholder="Type your text here..." rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Keywords</label>
            <textarea name="metaKeyword" value={formData.metaKeyword} onChange={handleInputChange} placeholder="Enter meta keywords" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
            <textarea name="metaDescription" value={formData.metaDescription} onChange={handleInputChange} placeholder="Enter meta Description" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => setShowBackModal(true)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={submitting} className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50">
            {submitting ? 'Updating...' : 'Submit'}
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showBackModal}
        title="Are you sure you want to go back?"
        message="Any unsaved changes to this state will be lost."
        confirmText="Yes, Go Back"
        cancelText="Stay Here"
        onConfirm={() => navigate(`/state/${formData.countryId}`)}
        onCancel={() => setShowBackModal(false)}
      />
    </div>
  );
};

export default EditState;
